import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Calculator, FileText, DollarSign, Mail, Menu, X, ChevronRight, TrendingUp, Shield, Clock, Send, ArrowRight, BookOpen, Lock, Star, CheckCircle, Calendar, CreditCard, ExternalLink, Download, Users, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "ca-pub-XXXXXXXXXXXXXXXX";

// Google AdSense Ad Component
const AdUnit = ({ slot, format = "auto", layout, className = "" }) => {
  const adRef = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (ADSENSE_CLIENT === "ca-pub-XXXXXXXXXXXXXXXX") return; // Skip if not configured
    try {
      if (!pushed.current && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      }
    } catch (e) { /* AdSense not loaded */ }
  }, []);

  // Show placeholder when AdSense isn't configured yet
  if (ADSENSE_CLIENT === "ca-pub-XXXXXXXXXXXXXXXX") {
    return (
      <div className={`bg-[#EAE7E1]/50 border border-dashed border-[#EAE7E1] rounded-lg p-4 text-center my-6 ${className}`} data-testid={`ad-placeholder-${slot || 'default'}`}>
        <p className="text-xs text-[#7B8D85]">Ad Space - Configure your AdSense Publisher ID to display ads here</p>
      </div>
    );
  }

  return (
    <div className={`my-6 ${className}`} data-testid={`ad-unit-${slot || 'default'}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot || ""}
        data-ad-format={format}
        {...(layout ? { "data-ad-layout": layout } : {})}
        data-full-width-responsive="true"
      />
    </div>
  );
};

// 2025 Federal Tax Brackets
const TAX_BRACKETS_2025 = {
  single: [
    { min: 0, max: 11925, rate: 0.10 },
    { min: 11925, max: 48475, rate: 0.12 },
    { min: 48475, max: 103350, rate: 0.22 },
    { min: 103350, max: 197300, rate: 0.24 },
    { min: 197300, max: 250525, rate: 0.32 },
    { min: 250525, max: 626350, rate: 0.35 },
    { min: 626350, max: Infinity, rate: 0.37 }
  ],
  married: [
    { min: 0, max: 23850, rate: 0.10 },
    { min: 23850, max: 96950, rate: 0.12 },
    { min: 96950, max: 206700, rate: 0.22 },
    { min: 206700, max: 394600, rate: 0.24 },
    { min: 394600, max: 501050, rate: 0.32 },
    { min: 501050, max: 751600, rate: 0.35 },
    { min: 751600, max: Infinity, rate: 0.37 }
  ]
};

// Affiliate Banner Component
const AffiliateBanner = ({ type }) => {
  const banners = {
    advisor: {
      title: "Need personalized guidance?",
      text: "Connect with a vetted fiduciary financial advisor who specializes in retirement tax planning.",
      cta: "Find an Advisor",
      link: "/find-advisor",
      external: false,
    },
    software: {
      title: "Tax Preparation Software",
      text: "File your taxes confidently with TurboTax - handles Roth conversions, RMDs, and Social Security income with ease.",
      cta: "Check Out TurboTax",
      link: "https://turbotax.intuit.com/?cid=all_smartretirementtaxes",
      external: true,
    },
    book: {
      title: "Recommended Reading",
      text: "\"The Power of Zero\" by David McKnight - The essential guide to achieving a tax-free retirement.",
      cta: "Get the Book on Amazon",
      link: "https://www.amazon.com/dp/1984823078?tag=smartretire02-20",
      external: true,
    }
  };
  
  const banner = banners[type] || banners.advisor;
  
  return (
    <div className="bg-gradient-to-r from-[#1A362D] to-[#4A6B5D] p-6 rounded-xl my-8" data-testid={`affiliate-banner-${type}`}>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#D4AF37] mb-1">Recommended Partner</p>
          <h4 className="text-white font-medium text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{banner.title}</h4>
          <p className="text-white/70 text-sm mt-1">{banner.text}</p>
        </div>
        {banner.external ? (
          <a
            href={banner.link}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#1A362D] px-6 py-3 rounded-lg font-medium hover:bg-[#D4AF37]/90 transition-all whitespace-nowrap"
            data-testid={`affiliate-cta-${type}`}
          >
            {banner.cta}
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : (
          <Link
            to={banner.link}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#1A362D] px-6 py-3 rounded-lg font-medium hover:bg-[#D4AF37]/90 transition-all whitespace-nowrap"
            data-testid={`affiliate-cta-${type}`}
          >
            {banner.cta}
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
};

// Premium Gate Component
const PremiumGate = ({ onUnlock, featureName }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/premium-access`, { email });
      toast.success("Access granted! Check your email for a welcome message.");
      onUnlock(email);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
        <div className="bg-white p-8 rounded-xl shadow-xl border border-[#EAE7E1] max-w-md w-full mx-4 text-center">
          <Lock className="w-12 h-12 text-[#1A362D] mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-xl font-medium text-[#1A362D] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Unlock {featureName}
          </h3>
          <p className="text-[#4A5D56] text-sm mb-6">
            Enter your email to get free access to our premium calculators and receive tax planning tips.
          </p>
          <form onSubmit={handleUnlock} className="space-y-3">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 border-[#EAE7E1] focus:ring-[#1A362D]"
              data-testid="premium-gate-email"
            />
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 w-full"
              data-testid="premium-gate-submit"
            >
              {loading ? "Unlocking..." : "Get Free Access"}
            </Button>
          </form>
          <p className="text-xs text-[#7B8D85] mt-3">No credit card required. You'll also receive our newsletter.</p>
        </div>
      </div>
      <div className="filter blur-sm pointer-events-none">
        <div className="bg-[#F9F8F6] p-8 rounded-lg border border-[#EAE7E1]">
          <div className="grid grid-cols-2 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white p-4 rounded-lg h-24" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="bg-[#F9F8F6]/95 backdrop-blur-xl border-b border-[#EAE7E1] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#1A362D] rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-semibold text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Smart Retirement Taxes
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/calculators" className="text-[#4A5D56] hover:text-[#1A362D] transition-colors" data-testid="nav-calculators">Calculators</Link>
            <Link to="/roth-strategies" className="text-[#4A5D56] hover:text-[#1A362D] transition-colors" data-testid="nav-roth">Roth Strategies</Link>
            <Link to="/social-security" className="text-[#4A5D56] hover:text-[#1A362D] transition-colors" data-testid="nav-social-security">Social Security</Link>
            <Link to="/rmd-planning" className="text-[#4A5D56] hover:text-[#1A362D] transition-colors" data-testid="nav-rmd">RMD Planning</Link>
            <Link to="/blog" className="text-[#4A5D56] hover:text-[#1A362D] transition-colors" data-testid="nav-blog">Blog</Link>
            <Link to="/guides">
              <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#1A362D] font-medium" data-testid="nav-guides-btn">
                Free Guides
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#1A362D] text-[#1A362D] hover:bg-[#1A362D] hover:text-white" data-testid="nav-contact-btn">
                Contact
              </Button>
            </Link>
          </div>

          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X className="w-6 h-6 text-[#1A362D]" /> : <Menu className="w-6 h-6 text-[#1A362D]" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#EAE7E1]">
            <div className="flex flex-col gap-4">
              {[
                { to: "/calculators", label: "Calculators" },
                { to: "/roth-strategies", label: "Roth Strategies" },
                { to: "/social-security", label: "Social Security" },
                { to: "/rmd-planning", label: "RMD Planning" },
                { to: "/blog", label: "Blog" },
                { to: "/guides", label: "Free Guides" },
                { to: "/find-advisor", label: "Find an Advisor" },
                { to: "/contact", label: "Contact" }
              ].map(l => (
                <Link key={l.to} to={l.to} className="text-[#4A5D56] hover:text-[#1A362D] py-2" onClick={() => setIsOpen(false)}>{l.label}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-[#1A362D] text-white py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="col-span-1 md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Smart Retirement Taxes</span>
          </div>
          <p className="text-white/70 max-w-md">Straightforward guides on Roth conversions, Social Security tax planning, RMD strategies, and minimizing taxes in retirement.</p>
        </div>
        <div>
          <h4 className="font-semibold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Resources</h4>
          <div className="flex flex-col gap-2">
            <Link to="/calculators" className="text-white/70 hover:text-white transition-colors">Calculators</Link>
            <Link to="/roth-strategies" className="text-white/70 hover:text-white transition-colors">Roth Strategies</Link>
            <Link to="/social-security" className="text-white/70 hover:text-white transition-colors">Social Security</Link>
            <Link to="/rmd-planning" className="text-white/70 hover:text-white transition-colors">RMD Planning</Link>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Company</h4>
          <div className="flex flex-col gap-2">
            <Link to="/blog" className="text-white/70 hover:text-white transition-colors">Blog</Link>
            <Link to="/guides" className="text-white/70 hover:text-white transition-colors">Free Guides</Link>
            <Link to="/find-advisor" className="text-white/70 hover:text-white transition-colors">Find an Advisor</Link>
            <Link to="/contact" className="text-white/70 hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-white/50 text-sm">
        <p>&copy; {new Date().getFullYear()} Smart Retirement Taxes. For educational purposes only.</p>
        <p className="mt-2 md:mt-0">Consult a qualified tax professional for personalized advice.</p>
      </div>
    </div>
  </footer>
);

// Newsletter Component
const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Successfully subscribed! Check your inbox for a welcome email.");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed.");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#EAE7E1] py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm border border-[#EAE7E1]">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="w-12 h-12 text-[#1A362D] mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-3xl md:text-4xl font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Get Tax Planning Tips in Your Inbox
            </h2>
            <p className="text-[#4A5D56] mb-8">Join thousands of retirees receiving our weekly insights on minimizing retirement taxes.</p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 border-[#EAE7E1] focus:ring-[#1A362D]" data-testid="newsletter-email-input" />
              <Button type="submit" disabled={loading} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 px-8" data-testid="newsletter-submit-btn">
                {loading ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => {
  const navigate = useNavigate();

  useEffect(() => {
    axios.post(`${API}/seed-blog`).then(() => axios.post(`${API}/seed-blog-additional`)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/30884f85-a420-495e-8243-8603b988be59/images/93ea6048608ed8b90be8aba0df296102a4d1b50f2747055add3f7454e4a68afe.png')` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A362D]/95 to-[#1A362D]/70" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-40">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4 fade-in">Retirement Tax Planning Made Simple</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium text-white leading-tight mb-6 fade-in stagger-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Reduce Retirement Tax Mistakes Before They Cost You
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl fade-in stagger-2">
              Straightforward guides on Roth conversions, Social Security tax planning, RMD strategies, and minimizing taxes in retirement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-in stagger-3">
              <Button onClick={() => navigate("/roth-strategies")} className="bg-white text-[#1A362D] hover:bg-white/90 h-14 px-8 text-lg" data-testid="hero-explore-btn">
                Explore Roth Strategies <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button onClick={() => navigate("/calculators")} variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-8 text-lg bg-transparent" data-testid="hero-calculator-btn">
                Try the Roth Calculator
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Plain English Explainer */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              New to Retirement Planning? Start Here.
            </h2>
            <p className="text-[#4A5D56] text-lg">No jargon. No financial degree required. Here's what you actually need to know.</p>
          </div>

          <div className="space-y-8">
            <div className="bg-[#F9F8F6] p-6 md:p-8 rounded-xl border border-[#EAE7E1]" data-testid="explainer-retirement-accounts">
              <h3 className="text-xl font-medium text-[#1A362D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What are retirement accounts and why do taxes matter?</h3>
              <p className="text-[#4A5D56] leading-relaxed">When you saved for retirement through a 401(k) or IRA at work, you got a tax break back then - you didn't pay taxes on that money when you put it in. The catch? <strong className="text-[#1A362D]">The IRS wants their share when you take it out.</strong> Every dollar you withdraw in retirement gets taxed like regular income. The goal of this site is to help you keep more of YOUR money and pay less to the IRS - legally.</p>
            </div>

            <div className="bg-[#F9F8F6] p-6 md:p-8 rounded-xl border border-[#EAE7E1]" data-testid="explainer-roth">
              <h3 className="text-xl font-medium text-[#1A362D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What's a Roth Conversion? (The short version)</h3>
              <p className="text-[#4A5D56] leading-relaxed mb-3">Think of it like this: You have money in a "taxed later" account (traditional IRA/401k). A Roth conversion moves some of that money into a "never taxed again" account (Roth IRA). <strong className="text-[#1A362D]">You pay tax on it now, but then it grows tax-free forever.</strong></p>
              <p className="text-[#4A5D56] leading-relaxed">Why bother? Because if you do it at the right time (like right after you retire but before Social Security kicks in), you might pay 12% tax now instead of 22% or more later. That's real money saved.</p>
            </div>

            <div className="bg-[#F9F8F6] p-6 md:p-8 rounded-xl border border-[#EAE7E1]" data-testid="explainer-social-security">
              <h3 className="text-xl font-medium text-[#1A362D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Wait, Social Security gets taxed too?</h3>
              <p className="text-[#4A5D56] leading-relaxed">Unfortunately, yes. If your total income is high enough, <strong className="text-[#1A362D]">up to 85% of your Social Security check can be taxed.</strong> That's money you already paid into your whole working life. The good news? With some planning, you can often reduce or even eliminate the tax on your Social Security benefits.</p>
            </div>

            <div className="bg-[#F9F8F6] p-6 md:p-8 rounded-xl border border-[#EAE7E1]" data-testid="explainer-rmd">
              <h3 className="text-xl font-medium text-[#1A362D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What's an RMD? (And why should I care?)</h3>
              <p className="text-[#4A5D56] leading-relaxed">RMD stands for Required Minimum Distribution. Starting at age 73, <strong className="text-[#1A362D]">the government FORCES you to take money out of your retirement accounts</strong> whether you need it or not - and you pay tax on every dollar. If you don't take enough out, the penalty is 25% of what you missed. Planning ahead can shrink these forced withdrawals significantly.</p>
            </div>

            <div className="bg-[#D4AF37]/10 p-6 md:p-8 rounded-xl border border-[#D4AF37]/30" data-testid="explainer-bottom-line">
              <h3 className="text-xl font-medium text-[#1A362D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The bottom line</h3>
              <p className="text-[#4A5D56] leading-relaxed mb-4">Without planning, the average retiree overpays thousands in taxes every year. You don't need to be a tax expert - you just need to understand a few key ideas and use the right tools.</p>
              <p className="text-[#4A5D56] leading-relaxed"><strong className="text-[#1A362D]">That's what this site is for.</strong> Free calculators to run your numbers. Simple guides that explain what to do and when. No sales pitch - just straightforward information to help you keep more of the money you worked hard to save.</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button onClick={() => navigate("/calculators")} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-14 px-8 text-lg" data-testid="explainer-cta">
              Try the Free Calculators <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Understanding these three areas can dramatically reduce the taxes you pay in retirement.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <Calculator className="w-8 h-8" strokeWidth={1.5} />, title: "Roth Conversion Strategies", desc: "Learn how converting traditional IRA or 401(k) funds to a Roth IRA may reduce lifetime taxes and improve retirement flexibility.", link: "/roth-strategies" },
              { icon: <Shield className="w-8 h-8" strokeWidth={1.5} />, title: "Social Security Tax Planning", desc: "Up to 85% of Social Security benefits may be taxable depending on your income. Learn strategies to reduce taxes.", link: "/social-security" },
              { icon: <Clock className="w-8 h-8" strokeWidth={1.5} />, title: "RMD Planning", desc: "After age 73 the IRS requires withdrawals from most retirement accounts. Learn how to manage RMDs and avoid unnecessary taxes.", link: "/rmd-planning" }
            ].map((f, i) => (
              <Card key={i} className="bg-white border-[#EAE7E1] hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer group" onClick={() => navigate(f.link)} data-testid={`feature-card-${i}`}>
                <CardHeader>
                  <div className="w-16 h-16 bg-[#1A362D] rounded-lg flex items-center justify-center text-white mb-4 group-hover:bg-[#4A6B5D] transition-colors">{f.icon}</div>
                  <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{f.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-[#4A5D56] text-base leading-relaxed">{f.desc}</CardDescription>
                  <div className="mt-6 flex items-center text-[#1A362D] font-medium group-hover:text-[#4A6B5D]">Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Free Guide CTA */}
      <section className="py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Download className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Free Retirement Tax Checklist
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Download our comprehensive checklist covering everything from Roth conversions to RMD planning. Plus, get our premium Roth Conversion Decision Guide.
          </p>
          <Button onClick={() => navigate("/guides")} className="bg-[#D4AF37] text-[#1A362D] hover:bg-[#D4AF37]/90 h-14 px-10 text-lg font-medium" data-testid="home-guides-cta">
            Get Your Free Guide <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Calculators CTA */}
      <section className="py-24 bg-[#EAE7E1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium text-[#1A362D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Free Retirement Tax Calculators</h2>
              <p className="text-[#4A5D56] text-lg mb-8">Use our suite of calculators to estimate the tax impact of your retirement decisions.</p>
              <Button onClick={() => navigate("/calculators")} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 px-6" data-testid="cta-calculators-btn">
                Explore All Calculators <Calculator className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <img src="https://static.prod-images.emergentagent.com/jobs/30884f85-a420-495e-8243-8603b988be59/images/a863906f441f4e29c6376d3948f7aa0c57e5695a1b8a875ab04b8d684f76f2b7.png" alt="Calculator preview" className="rounded-xl shadow-lg" />
          </div>
        </div>
      </section>

      <NewsletterSignup />
    </div>
  );
};

// ─── CALCULATORS ───

const CalculatorsPage = () => (
  <div className="min-h-screen bg-[#F9F8F6] py-12 md:py-20">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Retirement Tax Calculators</h1>
        <p className="text-[#4A5D56] text-lg max-w-2xl mx-auto">Estimate the tax impact of your retirement decisions with our free calculators.</p>
      </div>
      <Tabs defaultValue="roth" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-8 bg-[#EAE7E1]">
          <TabsTrigger value="roth" className="data-[state=active]:bg-[#1A362D] data-[state=active]:text-white" data-testid="tab-roth">Roth Conversion</TabsTrigger>
          <TabsTrigger value="social-security" className="data-[state=active]:bg-[#1A362D] data-[state=active]:text-white" data-testid="tab-social-security">Social Security Tax</TabsTrigger>
          <TabsTrigger value="rmd" className="data-[state=active]:bg-[#1A362D] data-[state=active]:text-white" data-testid="tab-rmd">RMD Calculator</TabsTrigger>
        </TabsList>
        <TabsContent value="roth"><RothConversionCalculator /></TabsContent>
        <TabsContent value="social-security"><SocialSecurityCalculator /></TabsContent>
        <TabsContent value="rmd"><RMDCalculator /></TabsContent>
      </Tabs>

      {/* Premium Multi-Year Projection */}
      <div className="mt-12">
        <PremiumMultiYearProjection />
      </div>

      <AdUnit slot="calculators-bottom" format="horizontal" />
      <AffiliateBanner type="advisor" />
    </div>
  </div>
);

// Roth Conversion Calculator
const RothConversionCalculator = () => {
  const [filingStatus, setFilingStatus] = useState("single");
  const [taxableIncome, setTaxableIncome] = useState("");
  const [conversionAmount, setConversionAmount] = useState("");
  const [iraBalance, setIraBalance] = useState("");
  const [targetBracket, setTargetBracket] = useState("0.22");
  const [stateTaxRate, setStateTaxRate] = useState("");
  const [age, setAge] = useState("");
  const [results, setResults] = useState(null);

  const calculateTax = (income, brackets) => {
    let tax = 0; let remainingIncome = income; let currentBracket = 0;
    for (const bracket of brackets) {
      if (remainingIncome <= 0) break;
      const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
      tax += taxableInBracket * bracket.rate;
      remainingIncome -= taxableInBracket;
      if (income > bracket.min) currentBracket = bracket.rate;
    }
    return { tax, bracket: currentBracket };
  };

  const findBracketLimit = (brackets, targetRate) => {
    for (const bracket of brackets) { if (bracket.rate === targetRate) return bracket.max; }
    return brackets[brackets.length - 1].max;
  };

  const handleCalculate = () => {
    const income = parseFloat(taxableIncome) || 0;
    const conversion = parseFloat(conversionAmount) || 0;
    const balance = parseFloat(iraBalance) || 0;
    const stateRate = parseFloat(stateTaxRate) / 100 || 0;
    const target = parseFloat(targetBracket);
    const brackets = filingStatus === "single" ? TAX_BRACKETS_2025.single : TAX_BRACKETS_2025.married;
    const beforeConversion = calculateTax(income, brackets);
    const afterConversion = calculateTax(income + conversion, brackets);
    const addedFederalTax = afterConversion.tax - beforeConversion.tax;
    const stateTax = conversion * stateRate;
    const totalTax = addedFederalTax + stateTax;
    const bracketLimit = findBracketLimit(brackets, target);
    const roomInBracket = Math.max(0, bracketLimit - (income + conversion));
    setResults({ addedFederalTax, stateTax, totalTax, marginalBracket: afterConversion.bracket, roomInBracket, percentConverted: balance > 0 ? (conversion / balance) * 100 : 0, netAmountMoved: conversion - totalTax });
  };

  const handleReset = () => { setTaxableIncome(""); setConversionAmount(""); setIraBalance(""); setStateTaxRate(""); setAge(""); setResults(null); };

  return (
    <Card className="bg-white border-[#EAE7E1]">
      <CardHeader>
        <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Roth Conversion Calculator</CardTitle>
        <CardDescription className="text-[#4A5D56]">Estimate the federal tax impact of a Roth conversion. Uses 2025 federal brackets.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-[#1A362D]">Enter your estimates</h3>
            <p className="text-sm text-[#7B8D85]">For educational planning only.</p>
            <div className="space-y-4">
              <div><Label className="text-[#1A362D]">Filing Status</Label>
                <Select value={filingStatus} onValueChange={setFilingStatus}><SelectTrigger className="h-12 border-[#EAE7E1]" data-testid="roth-filing-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="married">Married Filing Jointly</SelectItem></SelectContent></Select></div>
              <div><Label className="text-[#1A362D]">Expected taxable ordinary income ($)</Label><Input type="number" value={taxableIncome} onChange={(e) => setTaxableIncome(e.target.value)} placeholder="e.g., 50000" className="h-12 border-[#EAE7E1]" data-testid="roth-taxable-income" /></div>
              <div><Label className="text-[#1A362D]">Proposed Roth conversion ($)</Label><Input type="number" value={conversionAmount} onChange={(e) => setConversionAmount(e.target.value)} placeholder="e.g., 25000" className="h-12 border-[#EAE7E1]" data-testid="roth-conversion-amount" /></div>
              <div><Label className="text-[#1A362D]">Traditional IRA / 401(k) balance ($)</Label><Input type="number" value={iraBalance} onChange={(e) => setIraBalance(e.target.value)} placeholder="e.g., 500000" className="h-12 border-[#EAE7E1]" data-testid="roth-ira-balance" /></div>
              <div><Label className="text-[#1A362D]">Target top bracket</Label>
                <Select value={targetBracket} onValueChange={setTargetBracket}><SelectTrigger className="h-12 border-[#EAE7E1]" data-testid="roth-target-bracket"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0.12">12%</SelectItem><SelectItem value="0.22">22%</SelectItem><SelectItem value="0.24">24%</SelectItem><SelectItem value="0.32">32%</SelectItem><SelectItem value="0.35">35%</SelectItem><SelectItem value="0.37">37%</SelectItem></SelectContent></Select></div>
              <div><Label className="text-[#1A362D]">Optional state tax rate (%)</Label><Input type="number" value={stateTaxRate} onChange={(e) => setStateTaxRate(e.target.value)} placeholder="e.g., 5" className="h-12 border-[#EAE7E1]" data-testid="roth-state-tax" /></div>
              <div><Label className="text-[#1A362D]">Age (optional)</Label><Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="e.g., 62" className="h-12 border-[#EAE7E1]" data-testid="roth-age" /></div>
              <div className="flex gap-4 pt-4">
                <Button onClick={handleCalculate} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 flex-1" data-testid="roth-calc-submit">Calculate</Button>
                <Button onClick={handleReset} variant="outline" className="border-[#1A362D] text-[#1A362D] hover:bg-[#1A362D] hover:text-white h-12" data-testid="roth-calc-reset">Reset</Button>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            {results ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">Added federal tax</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="result-federal-tax">${results.addedFederalTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">State tax</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="result-state-tax">${results.stateTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#1A362D] p-4 rounded-lg col-span-2"><p className="text-sm text-white/70 mb-1">Total estimated tax</p><p className="text-3xl font-semibold text-white" data-testid="result-total-tax">${results.totalTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">Marginal bracket</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="result-marginal-bracket">{(results.marginalBracket * 100).toFixed(0)}%</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">Room in target bracket</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="result-room-in-bracket">${results.roomInBracket.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">% of account converted</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="result-percent-converted">{results.percentConverted.toFixed(1)}%</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">Net amount moved</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="result-net-amount">${results.netAmountMoved.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
              </div>
            ) : (
              <div className="bg-[#F9F8F6] p-8 rounded-lg border border-[#EAE7E1] text-center">
                <Calculator className="w-16 h-16 text-[#1A362D]/30 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#7B8D85]">Enter your information and click Calculate to see results.</p>
              </div>
            )}
            <div className="bg-[#D4AF37]/10 p-4 rounded-lg border border-[#D4AF37]/30"><p className="text-sm text-[#1A362D]"><strong>Good rule of thumb:</strong> Convert only enough each year to fill up a target bracket.</p></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Social Security Calculator
const SocialSecurityCalculator = () => {
  const [filingStatus, setFilingStatus] = useState("single");
  const [ssaBenefit, setSsaBenefit] = useState("");
  const [otherIncome, setOtherIncome] = useState("");
  const [taxExemptInterest, setTaxExemptInterest] = useState("");
  const [results, setResults] = useState(null);

  const calculateSSTax = () => {
    const ssa = parseFloat(ssaBenefit) || 0;
    const other = parseFloat(otherIncome) || 0;
    const exempt = parseFloat(taxExemptInterest) || 0;
    const provisionalIncome = other + exempt + (ssa * 0.5);
    const thresholds = filingStatus === "single" ? { lower: 25000, upper: 34000 } : { lower: 32000, upper: 44000 };
    let taxablePercent = 0; let taxableBenefits = 0;
    if (provisionalIncome <= thresholds.lower) { taxablePercent = 0; taxableBenefits = 0; }
    else if (provisionalIncome <= thresholds.upper) { taxablePercent = 50; taxableBenefits = Math.min(ssa * 0.5, (provisionalIncome - thresholds.lower) * 0.5); }
    else { taxablePercent = 85; const base = Math.min(ssa * 0.5, (thresholds.upper - thresholds.lower) * 0.5); taxableBenefits = Math.min(ssa * 0.85, base + (provisionalIncome - thresholds.upper) * 0.85); }
    setResults({ provisionalIncome, taxablePercent, taxableBenefits, taxFreeBenefits: ssa - taxableBenefits });
  };

  return (
    <Card className="bg-white border-[#EAE7E1]">
      <CardHeader>
        <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Social Security Tax Calculator</CardTitle>
        <CardDescription className="text-[#4A5D56]">Estimate how much of your Social Security benefits may be taxable.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div><Label className="text-[#1A362D]">Filing Status</Label>
              <Select value={filingStatus} onValueChange={setFilingStatus}><SelectTrigger className="h-12 border-[#EAE7E1]" data-testid="ss-filing-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="married">Married Filing Jointly</SelectItem></SelectContent></Select></div>
            <div><Label className="text-[#1A362D]">Annual Social Security Benefit ($)</Label><Input type="number" value={ssaBenefit} onChange={(e) => setSsaBenefit(e.target.value)} placeholder="e.g., 30000" className="h-12 border-[#EAE7E1]" data-testid="ss-benefit-input" /></div>
            <div><Label className="text-[#1A362D]">Other Taxable Income ($)</Label><Input type="number" value={otherIncome} onChange={(e) => setOtherIncome(e.target.value)} placeholder="e.g., 40000" className="h-12 border-[#EAE7E1]" data-testid="ss-other-income" /><p className="text-xs text-[#7B8D85] mt-1">Pensions, IRA distributions, wages, etc.</p></div>
            <div><Label className="text-[#1A362D]">Tax-Exempt Interest ($)</Label><Input type="number" value={taxExemptInterest} onChange={(e) => setTaxExemptInterest(e.target.value)} placeholder="e.g., 5000" className="h-12 border-[#EAE7E1]" data-testid="ss-tax-exempt" /></div>
            <div className="flex gap-4 pt-4">
              <Button onClick={calculateSSTax} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 flex-1" data-testid="ss-calc-submit">Calculate</Button>
              <Button onClick={() => { setSsaBenefit(""); setOtherIncome(""); setTaxExemptInterest(""); setResults(null); }} variant="outline" className="border-[#1A362D] text-[#1A362D] hover:bg-[#1A362D] hover:text-white h-12" data-testid="ss-calc-reset">Reset</Button>
            </div>
          </div>
          <div className="space-y-6">
            {results ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1] col-span-2"><p className="text-sm text-[#7B8D85] mb-1">Provisional Income</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="ss-result-provisional">${results.provisionalIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#1A362D] p-4 rounded-lg"><p className="text-sm text-white/70 mb-1">Taxable %</p><p className="text-3xl font-semibold text-white" data-testid="ss-result-percent">{results.taxablePercent}%</p></div>
                <div className="bg-[#4A6B5D] p-4 rounded-lg"><p className="text-sm text-white/70 mb-1">Taxable Benefits</p><p className="text-2xl font-semibold text-white" data-testid="ss-result-taxable">${results.taxableBenefits.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1] col-span-2"><p className="text-sm text-[#7B8D85] mb-1">Tax-Free Benefits</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="ss-result-tax-free">${results.taxFreeBenefits.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
              </div>
            ) : (
              <div className="bg-[#F9F8F6] p-8 rounded-lg border border-[#EAE7E1] text-center"><Shield className="w-16 h-16 text-[#1A362D]/30 mx-auto mb-4" strokeWidth={1.5} /><p className="text-[#7B8D85]">Enter your information to see results.</p></div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// RMD Calculator
const RMDCalculator = () => {
  const [age, setAge] = useState("");
  const [accountBalance, setAccountBalance] = useState("");
  const [results, setResults] = useState(null);

  const getDistributionPeriod = (age) => {
    const table = { 72:27.4,73:26.5,74:25.5,75:24.6,76:23.7,77:22.9,78:22.0,79:21.1,80:20.2,81:19.4,82:18.5,83:17.7,84:16.8,85:16.0,86:15.2,87:14.4,88:13.7,89:12.9,90:12.2,91:11.5,92:10.8,93:10.1,94:9.5,95:8.9,96:8.4,97:7.8,98:7.3,99:6.8,100:6.4 };
    return table[age] || (age < 72 ? 27.4 : 6.4);
  };

  const calculateRMD = () => {
    const ageNum = parseInt(age) || 0;
    const balance = parseFloat(accountBalance) || 0;
    if (ageNum < 73) { setResults({ rmdRequired: false, message: "RMDs are not required until age 73. This is prime time for Roth conversions." }); return; }
    const dp = getDistributionPeriod(ageNum);
    setResults({ rmdRequired: true, distributionPeriod: dp, rmdAmount: balance / dp, withdrawalPercent: (1 / dp) * 100, remainingBalance: balance - (balance / dp) });
  };

  return (
    <Card className="bg-white border-[#EAE7E1]">
      <CardHeader>
        <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>RMD Calculator</CardTitle>
        <CardDescription className="text-[#4A5D56]">Calculate your Required Minimum Distribution.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div><Label className="text-[#1A362D]">Your Age</Label><Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="e.g., 75" className="h-12 border-[#EAE7E1]" data-testid="rmd-age-input" /></div>
            <div><Label className="text-[#1A362D]">Account Balance ($)</Label><Input type="number" value={accountBalance} onChange={(e) => setAccountBalance(e.target.value)} placeholder="e.g., 500000" className="h-12 border-[#EAE7E1]" data-testid="rmd-balance-input" /><p className="text-xs text-[#7B8D85] mt-1">As of Dec 31 of the previous year</p></div>
            <div className="flex gap-4 pt-4">
              <Button onClick={calculateRMD} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 flex-1" data-testid="rmd-calc-submit">Calculate</Button>
              <Button onClick={() => { setAge(""); setAccountBalance(""); setResults(null); }} variant="outline" className="border-[#1A362D] text-[#1A362D] hover:bg-[#1A362D] hover:text-white h-12" data-testid="rmd-calc-reset">Reset</Button>
            </div>
          </div>
          <div className="space-y-6">
            {results ? (results.rmdRequired ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#1A362D] p-4 rounded-lg col-span-2"><p className="text-sm text-white/70 mb-1">Required Minimum Distribution</p><p className="text-3xl font-semibold text-white" data-testid="rmd-result-amount">${results.rmdAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">Distribution Period</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="rmd-result-period">{results.distributionPeriod} yrs</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1]"><p className="text-sm text-[#7B8D85] mb-1">Withdrawal %</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="rmd-result-percent">{results.withdrawalPercent.toFixed(2)}%</p></div>
                <div className="bg-[#F9F8F6] p-4 rounded-lg border border-[#EAE7E1] col-span-2"><p className="text-sm text-[#7B8D85] mb-1">Remaining Balance</p><p className="text-2xl font-semibold text-[#1A362D]" data-testid="rmd-result-remaining">${results.remainingBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
              </div>
            ) : (
              <div className="bg-[#D4AF37]/10 p-6 rounded-lg border border-[#D4AF37]/30"><TrendingUp className="w-8 h-8 text-[#D4AF37] mb-2" /><p className="text-[#1A362D]" data-testid="rmd-no-rmd-message">{results.message}</p></div>
            )) : (
              <div className="bg-[#F9F8F6] p-8 rounded-lg border border-[#EAE7E1] text-center"><Clock className="w-16 h-16 text-[#1A362D]/30 mx-auto mb-4" strokeWidth={1.5} /><p className="text-[#7B8D85]">Enter your information to see results.</p></div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Premium Multi-Year Projection (gated)
const PremiumMultiYearProjection = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [filingStatus, setFilingStatus] = useState("single");
  const [currentAge, setCurrentAge] = useState("");
  const [iraBalance, setIraBalance] = useState("");
  const [annualConversion, setAnnualConversion] = useState("");
  const [growthRate, setGrowthRate] = useState("6");
  const [projections, setProjections] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem("premium_email");
    if (saved) { setUnlocked(true); setEmail(saved); }
  }, []);

  const handleUnlock = (unlockedEmail) => {
    setUnlocked(true);
    setEmail(unlockedEmail);
    localStorage.setItem("premium_email", unlockedEmail);
  };

  const calculateProjection = () => {
    const startAge = parseInt(currentAge) || 62;
    const balance = parseFloat(iraBalance) || 0;
    const annual = parseFloat(annualConversion) || 0;
    const growth = parseFloat(growthRate) / 100 || 0.06;
    const brackets = filingStatus === "single" ? TAX_BRACKETS_2025.single : TAX_BRACKETS_2025.married;
    
    const years = [];
    let remaining = balance;
    let totalTaxPaid = 0;
    let totalConverted = 0;

    for (let y = 0; y < 10 && remaining > 0; y++) {
      const convertAmount = Math.min(annual, remaining);
      let tax = 0; let rem = convertAmount;
      for (const b of brackets) { if (rem <= 0) break; const t = Math.min(rem, b.max - b.min); tax += t * b.rate; rem -= t; }
      remaining = (remaining - convertAmount) * (1 + growth);
      totalTaxPaid += tax;
      totalConverted += convertAmount;
      years.push({ age: startAge + y, year: 2025 + y, converted: convertAmount, tax, remaining: Math.max(0, remaining), totalConverted, totalTax: totalTaxPaid });
    }
    setProjections(years);
  };

  if (!unlocked) {
    return (
      <Card className="bg-white border-[#EAE7E1]">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Premium: Multi-Year Conversion Projection
            </CardTitle>
          </div>
          <CardDescription className="text-[#4A5D56]">See how annual Roth conversions could reduce your traditional balance over 10 years.</CardDescription>
        </CardHeader>
        <CardContent>
          <PremiumGate onUnlock={handleUnlock} featureName="Multi-Year Projection" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#EAE7E1] border-l-4 border-l-[#D4AF37]">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-[#D4AF37]" />
          <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Premium: Multi-Year Conversion Projection
          </CardTitle>
        </div>
        <CardDescription className="text-[#4A5D56]">See how annual conversions could reduce your traditional balance over 10 years.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div><Label>Filing Status</Label><Select value={filingStatus} onValueChange={setFilingStatus}><SelectTrigger className="h-12 border-[#EAE7E1]" data-testid="premium-filing-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="married">Married Filing Jointly</SelectItem></SelectContent></Select></div>
            <div><Label>Current Age</Label><Input type="number" value={currentAge} onChange={(e) => setCurrentAge(e.target.value)} placeholder="e.g., 62" className="h-12 border-[#EAE7E1]" data-testid="premium-age" /></div>
            <div><Label>Total IRA/401(k) Balance ($)</Label><Input type="number" value={iraBalance} onChange={(e) => setIraBalance(e.target.value)} placeholder="e.g., 500000" className="h-12 border-[#EAE7E1]" data-testid="premium-balance" /></div>
            <div><Label>Annual Conversion Amount ($)</Label><Input type="number" value={annualConversion} onChange={(e) => setAnnualConversion(e.target.value)} placeholder="e.g., 50000" className="h-12 border-[#EAE7E1]" data-testid="premium-annual" /></div>
            <div><Label>Assumed Growth Rate (%)</Label><Input type="number" value={growthRate} onChange={(e) => setGrowthRate(e.target.value)} placeholder="e.g., 6" className="h-12 border-[#EAE7E1]" data-testid="premium-growth" /></div>
            <Button onClick={calculateProjection} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 w-full" data-testid="premium-calc-submit">Run 10-Year Projection</Button>
          </div>
          <div>
            {projections ? (
              <div className="space-y-4">
                <div className="bg-[#1A362D] p-4 rounded-lg">
                  <p className="text-sm text-white/70">Total Converted Over {projections.length} Years</p>
                  <p className="text-2xl font-semibold text-white" data-testid="premium-total-converted">${projections[projections.length-1].totalConverted.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                </div>
                <div className="bg-[#4A6B5D] p-4 rounded-lg">
                  <p className="text-sm text-white/70">Total Taxes Paid</p>
                  <p className="text-2xl font-semibold text-white" data-testid="premium-total-tax">${projections[projections.length-1].totalTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                </div>
                <div className="overflow-auto max-h-72">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#EAE7E1]"><th className="text-left py-2 text-[#7B8D85]">Year</th><th className="text-left py-2 text-[#7B8D85]">Age</th><th className="text-right py-2 text-[#7B8D85]">Converted</th><th className="text-right py-2 text-[#7B8D85]">Tax</th><th className="text-right py-2 text-[#7B8D85]">Remaining</th></tr></thead>
                    <tbody>{projections.map((p, i) => (
                      <tr key={i} className="border-b border-[#EAE7E1]/50"><td className="py-2 text-[#1A362D]">{p.year}</td><td className="py-2 text-[#1A362D]">{p.age}</td><td className="text-right py-2 text-[#1A362D]">${p.converted.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td><td className="text-right py-2 text-[#4A5D56]">${p.tax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td><td className="text-right py-2 text-[#1A362D]">${p.remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-[#F9F8F6] p-8 rounded-lg border border-[#EAE7E1] text-center">
                <TrendingUp className="w-16 h-16 text-[#1A362D]/30 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#7B8D85]">Fill in your details and click Run Projection.</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Guides Page (Free + Paid PDFs)
const GuidesPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [paidLoading, setPaidLoading] = useState(false);
  const [paidEmail, setPaidEmail] = useState("");

  const handleFreeDownload = async () => {
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/download/retirement-checklist`, { email }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Retirement_Tax_Checklist.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloading! Check your inbox for a welcome email too.");
      setEmail("");
    } catch { toast.error("Download failed. Please try again."); }
    finally { setLoading(false); }
  };

  const handlePaidCheckout = async () => {
    if (!paidEmail) { toast.error("Please enter your email."); return; }
    setPaidLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/products/checkout`, { product_id: "roth-conversion-guide", origin_url: originUrl, email: paidEmail });
      if (response.data.url) window.location.href = response.data.url;
    } catch { toast.error("Checkout failed. Please try again."); }
    finally { setPaidLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Download className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Retirement Tax Planning Guides</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">Download our expert guides to take control of your retirement tax planning.</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Free Guide */}
            <Card className="bg-white border-[#EAE7E1] border-t-4 border-t-[#4A6B5D] relative overflow-hidden" data-testid="free-guide-card">
              <div className="absolute top-4 right-4 bg-[#4A6B5D] text-white text-xs font-bold px-3 py-1 rounded-full">FREE</div>
              <CardHeader>
                <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The Complete Retirement Tax Checklist</CardTitle>
                <CardDescription className="text-[#4A5D56]">Your step-by-step guide to minimizing taxes before, during, and after retirement.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {["Pre-retirement tax diversification checklist", "Roth conversion gap years strategy", "RMD management and QCD planning", "Social Security tax optimization", "Annual tax review framework", "2025 tax brackets and key numbers"].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#4A5D56]"><CheckCircle className="w-4 h-4 text-[#4A6B5D] flex-shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <div className="space-y-3">
                  <Input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 border-[#EAE7E1]" data-testid="free-guide-email" />
                  <Button onClick={handleFreeDownload} disabled={loading} className="bg-[#4A6B5D] hover:bg-[#1A362D] text-white h-12 w-full" data-testid="free-guide-download-btn">
                    {loading ? "Preparing..." : "Download Free PDF"} <Download className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-[#7B8D85] mt-3 text-center">Free. No credit card. You'll also get our newsletter.</p>
              </CardContent>
            </Card>

            {/* Paid Guide */}
            <Card className="bg-white border-[#EAE7E1] border-t-4 border-t-[#D4AF37] relative overflow-hidden" data-testid="paid-guide-card">
              <div className="absolute top-4 right-4 bg-[#D4AF37] text-[#1A362D] text-xs font-bold px-3 py-1 rounded-full">PREMIUM</div>
              <CardHeader>
                <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The Roth Conversion Decision Guide</CardTitle>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-3xl font-semibold text-[#1A362D]">$17</span>
                  <span className="text-sm text-[#7B8D85] line-through">$47</span>
                </div>
                <CardDescription className="text-[#4A5D56]">A comprehensive framework for deciding if, when, and how much to convert.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {["Should-you-convert decision framework", "Bracket-filling strategy with examples", "IRMAA impact calculations", "Multi-year conversion planning worksheet", "Pro-rata rule workarounds", "Estate planning benefits analysis"].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#4A5D56]"><CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <div className="space-y-3">
                  <Input type="email" placeholder="Enter your email" value={paidEmail} onChange={(e) => setPaidEmail(e.target.value)} className="h-12 border-[#EAE7E1]" data-testid="paid-guide-email" />
                  <Button onClick={handlePaidCheckout} disabled={paidLoading} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#1A362D] h-12 w-full font-medium" data-testid="paid-guide-checkout-btn">
                    {paidLoading ? "Processing..." : "Buy Now - $17"} <CreditCard className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-[#7B8D85] mt-3 text-center">Secure payment via Stripe. Instant PDF download after payment.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-12"><AdUnit slot="guides-bottom" format="horizontal" /></div>
      <AffiliateBanner type="advisor" />
      <NewsletterSignup />
    </div>
  );
};

// Download Success (paid product)
const DownloadSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");
  const productId = searchParams.get("product");

  const pollAndDownload = useCallback(async (attempts = 0) => {
    if (!sessionId || attempts >= 5) { setStatus(attempts >= 5 ? "timeout" : "error"); return; }
    try {
      const res = await axios.get(`${API}/consultation/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatus("paid");
        // Auto download
        try {
          const dlRes = await axios.get(`${API}/products/download/${productId}?session_id=${sessionId}`, { responseType: 'blob' });
          const url = window.URL.createObjectURL(new Blob([dlRes.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Roth_Conversion_Decision_Guide.pdf');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch { /* download failed, user can retry */ }
      } else if (res.data.status === "expired") { setStatus("expired"); }
      else { setTimeout(() => pollAndDownload(attempts + 1), 2000); }
    } catch { setStatus("error"); }
  }, [sessionId, productId]);

  useEffect(() => { pollAndDownload(); }, [pollAndDownload]);

  const handleRetryDownload = async () => {
    try {
      const dlRes = await axios.get(`${API}/products/download/${productId}?session_id=${sessionId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([dlRes.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Roth_Conversion_Decision_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download started!");
    } catch { toast.error("Download failed. Please contact support."); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center py-20">
      <div className="max-w-lg mx-auto px-6 text-center">
        {status === "checking" && <><div className="w-16 h-16 border-4 border-[#1A362D] border-t-transparent rounded-full animate-spin mx-auto mb-6" /><h2 className="text-2xl font-medium text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Processing your payment...</h2></>}
        {status === "paid" && (
          <div className="bg-white p-8 rounded-xl border border-[#EAE7E1] shadow-sm">
            <CheckCircle className="w-16 h-16 text-[#4A6B5D] mx-auto mb-4" />
            <h2 className="text-2xl font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Thank You!</h2>
            <p className="text-[#4A5D56] mb-6">Your download should start automatically. If not, click below:</p>
            <Button onClick={handleRetryDownload} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white" data-testid="retry-download-btn">
              <Download className="mr-2 w-4 h-4" /> Download PDF
            </Button>
            <div className="mt-6"><Link to="/"><Button variant="outline" className="border-[#1A362D] text-[#1A362D]">Back to Home</Button></Link></div>
          </div>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <div className="bg-white p-8 rounded-xl border border-[#EAE7E1]">
            <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-medium text-[#1A362D] mb-4">Payment Issue</h2>
            <p className="text-[#4A5D56] mb-6">Please try again or contact us for help.</p>
            <Link to="/guides"><Button className="bg-[#1A362D] text-white">Back to Guides</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Find an Advisor (Affiliate) Page
const FindAdvisorPage = () => (
  <div className="min-h-screen bg-[#F9F8F6]">
    <section className="relative py-20 bg-[#1A362D]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
        <Users className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
        <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Find a Retirement Tax Specialist</h1>
        <p className="text-lg text-white/80 max-w-2xl mx-auto">Connect with vetted financial advisors and CPAs who specialize in retirement tax planning.</p>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {[
            { name: "Boldin Retirement Planner", desc: "Comprehensive retirement planning software with detailed tax projections, Roth conversion modeling, and Social Security optimization tools.", cta: "Try Boldin Free", link: "https://www.boldin.com/retirement/planner-signup/", badge: "Recommended" },
            { name: "NerdWallet Advisor Match", desc: "Get matched with vetted financial advisors based on your specific retirement planning needs. Free matching service.", cta: "Find an Advisor", link: "https://www.nerdwallet.com/advisors", badge: "Free Matching" },
            { name: "NAPFA Advisor Search", desc: "Find fee-only financial advisors through the National Association of Personal Financial Advisors directory.", cta: "Search Advisors", link: "https://www.napfa.org/find-an-advisor", badge: "Fee-Only" },
            { name: "Find a CPA (AICPA)", desc: "Search for CPAs with retirement tax specialization through the American Institute of CPAs directory.", cta: "Find a CPA", link: "https://www.aicpa-cima.com/resources/find-a-cpa", badge: "Tax Specialists" }
          ].map((advisor, i) => (
            <Card key={i} className="bg-white border-[#EAE7E1] hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`advisor-card-${i}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{advisor.name}</CardTitle>
                  <span className="text-xs bg-[#EAE7E1] text-[#1A362D] px-3 py-1 rounded-full font-medium">{advisor.badge}</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#4A5D56] mb-6">{advisor.desc}</p>
                <a href={advisor.link} target="_blank" rel="noopener noreferrer sponsored" className="inline-flex items-center gap-2 bg-[#1A362D] text-white px-6 py-3 rounded-lg hover:bg-[#4A6B5D] transition-colors w-full justify-center" data-testid={`advisor-cta-${i}`}>
                  {advisor.cta} <ExternalLink className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-[#D4AF37]/10 p-8 rounded-xl border border-[#D4AF37]/30 text-center">
          <h3 className="text-xl font-medium text-[#1A362D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>How to Choose the Right Advisor</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 text-left">
            {[
              { title: "Look for Fiduciary Status", desc: "A fiduciary is legally required to act in your best interest, not just recommend 'suitable' products." },
              { title: "Ask About Specialization", desc: "Not all advisors understand retirement-specific tax strategies. Ask specifically about Roth conversions, RMDs, and SS optimization." },
              { title: "Understand the Fee Structure", desc: "Fee-only advisors don't earn commissions on products. This eliminates conflicts of interest." }
            ].map((tip, i) => (
              <div key={i}><h4 className="font-medium text-[#1A362D] mb-2">{tip.title}</h4><p className="text-sm text-[#4A5D56]">{tip.desc}</p></div>
            ))}
          </div>
        </div>

        <AffiliateBanner type="software" />

        {/* Recommended Books */}
        <div className="mt-12">
          <h2 className="text-2xl font-medium text-[#1A362D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Recommended Books</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Retirement Savings: Time Ticks Louder", author: "Ed Slott", link: "https://www.amazon.com/dp/0143138502?tag=smartretire02-20", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=200&h=280&fit=crop" },
              { title: "The Power of Zero", author: "David McKnight", link: "https://www.amazon.com/dp/1984823078?tag=smartretire02-20", img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=200&h=280&fit=crop" },
              { title: "Stay Rich for Life", author: "Ed Slott", link: "https://www.amazon.com/dp/0345514432?tag=smartretire02-20", img: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=200&h=280&fit=crop" },
              { title: "How to Make Your Money Last", author: "Jane Bryant Quinn", link: "https://www.amazon.com/dp/1982115831?tag=smartretire02-20", img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=200&h=280&fit=crop" }
            ].map((book, i) => (
              <a key={i} href={book.link} target="_blank" rel="noopener noreferrer sponsored" className="bg-white p-4 rounded-lg border border-[#EAE7E1] hover:-translate-y-1 hover:shadow-lg transition-all duration-300 block" data-testid={`book-link-${i}`}>
                <div className="aspect-[3/4] bg-[#EAE7E1] rounded-md mb-3 overflow-hidden"><img src={book.img} alt={book.title} className="w-full h-full object-cover" /></div>
                <h4 className="font-medium text-[#1A362D] text-sm leading-snug">{book.title}</h4>
                <p className="text-xs text-[#7B8D85] mt-1">{book.author}</p>
                <p className="text-xs text-[#D4AF37] mt-2 font-medium">View on Amazon</p>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>

    <NewsletterSignup />
  </div>
);

// ─── CONTENT PAGES ───

const RothStrategiesPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">Tax Planning Strategy</p>
          <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Roth Conversion Strategies</h1>
          <p className="text-lg text-white/80 max-w-2xl">Learn how converting traditional IRA or 401(k) funds to a Roth IRA may reduce lifetime taxes and improve retirement flexibility.</p>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <img src="https://images.unsplash.com/photo-1746192703851-2da032ca7263" alt="Retirement planning" className="w-full rounded-xl mb-8 shadow-lg" />
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What is a Roth Conversion?</h2>
          <p className="text-[#4A5D56] mb-6 leading-relaxed">A Roth conversion involves moving funds from a traditional IRA or 401(k) into a Roth IRA. You pay taxes on the converted amount now, but future qualified withdrawals—including all growth—are completely tax-free.</p>
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6 mt-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Why Consider Roth Conversions?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {[{ t: "Tax-Free Growth", d: "All future growth in your Roth IRA is completely tax-free." }, { t: "No RMDs", d: "Roth IRAs have no required minimum distributions during your lifetime." }, { t: "Estate Planning", d: "Heirs receive tax-free withdrawals from inherited Roth IRAs." }, { t: "Tax Diversification", d: "Having both pre-tax and Roth funds gives flexibility in retirement." }].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-lg border border-[#EAE7E1]"><h4 className="font-medium text-[#1A362D] mb-2">{item.t}</h4><p className="text-sm text-[#4A5D56]">{item.d}</p></div>
            ))}
          </div>
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6 mt-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The "Gap Years" Strategy</h2>
          <p className="text-[#4A5D56] mb-6 leading-relaxed">The ideal time for many retirees to convert is during the "gap years" between retirement and when Social Security benefits and RMDs begin. During this period, your income may be lower than at any other time, potentially putting you in a lower tax bracket.</p>
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6 mt-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Bracket-Filling Strategy</h2>
          <p className="text-[#4A5D56] mb-6 leading-relaxed">The most common approach is to convert just enough each year to "fill up" a target tax bracket without spilling over. For example, if you're in the 12% bracket with room before hitting 22%, convert up to that threshold.</p>
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6 mt-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Key Considerations</h2>
          <ul className="space-y-4 text-[#4A5D56] mb-8">
            {["Medicare IRMAA: Large conversions can increase Medicare premiums two years later.", "State Taxes: Some states have no income tax, which could affect your strategy.", "Tax Payment Source: If possible, pay conversion taxes from non-retirement funds.", "Time Horizon: The longer your money can grow tax-free, the more beneficial.", "Five-Year Rule: Converted funds must be in the Roth for 5 years before penalty-free withdrawal (if under 59½).", "Pro-Rata Rule: If you have both pre-tax and after-tax money in your IRA, the conversion is taxed proportionally."].map((item, i) => (
              <li key={i} className="flex items-start gap-3"><ChevronRight className="w-5 h-5 text-[#1A362D] flex-shrink-0 mt-1" /><span><strong className="text-[#1A362D]">{item.split(':')[0]}:</strong>{item.split(':').slice(1).join(':')}</span></li>
            ))}
          </ul>
          <AdUnit slot="roth-strategies-mid" format="horizontal" />
          <AffiliateBanner type="advisor" />
          <div className="mt-8 p-8 bg-[#1A362D] rounded-xl text-center">
            <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ready to estimate your conversion?</h3>
            <Button onClick={() => navigate("/calculators")} className="bg-white text-[#1A362D] hover:bg-white/90 h-12 px-8" data-testid="roth-page-calculator-btn">Use the Roth Calculator <Calculator className="ml-2 w-5 h-5" /></Button>
          </div>
        </div>
      </section>
      <NewsletterSignup />
    </div>
  );
};

const SocialSecurityPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">Tax Planning Strategy</p>
          <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Social Security Tax Planning</h1>
          <p className="text-lg text-white/80 max-w-2xl">Up to 85% of Social Security benefits may be taxable. Learn strategies to reduce taxes on your retirement benefits.</p>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>How Social Security Benefits Are Taxed</h2>
          <p className="text-[#4A5D56] mb-6 leading-relaxed">Many retirees are surprised to learn their Social Security benefits may be subject to federal income tax. The amount taxed depends on your "provisional income."</p>
          <div className="bg-white p-6 rounded-lg border border-[#EAE7E1] mb-8">
            <p className="text-[#1A362D] font-medium mb-4">Provisional income includes:</p>
            <ul className="space-y-2 text-[#4A5D56]"><li>• Adjusted Gross Income (excluding Social Security)</li><li>• Tax-exempt interest (municipal bonds)</li><li>• 50% of your Social Security benefits</li></ul>
          </div>
          <h3 className="text-xl font-medium text-[#1A362D] mb-4 mt-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Taxation Thresholds</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg border border-[#EAE7E1]"><h4 className="font-medium text-[#1A362D] mb-3">Single Filers</h4><ul className="space-y-2 text-sm text-[#4A5D56]"><li>Below $25,000: 0% taxable</li><li>$25,000 - $34,000: Up to 50%</li><li>Above $34,000: Up to 85%</li></ul></div>
            <div className="bg-white p-6 rounded-lg border border-[#EAE7E1]"><h4 className="font-medium text-[#1A362D] mb-3">Married Filing Jointly</h4><ul className="space-y-2 text-sm text-[#4A5D56]"><li>Below $32,000: 0% taxable</li><li>$32,000 - $44,000: Up to 50%</li><li>Above $44,000: Up to 85%</li></ul></div>
          </div>
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6 mt-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Strategies to Reduce Social Security Taxes</h2>
          <div className="space-y-6">
            {[{ t: "Roth Conversions Before Claiming", d: "Convert traditional IRA funds to Roth before claiming Social Security to reduce future provisional income." }, { t: "Strategic Withdrawal Sequencing", d: "The order you draw from accounts matters. Consider taxable first, then tax-deferred, then Roth." }, { t: "Timing Your Benefits", d: "Delaying Social Security can sometimes help if you have significant other income in early retirement." }, { t: "Qualified Charitable Distributions", d: "If you're 70½+, donating IRA funds directly to charity reduces taxable income without affecting SS taxation." }, { t: "Roth Withdrawals Don't Count", d: "Roth IRA withdrawals do not count toward provisional income. This makes them ideal for supplementing SS income." }].map((s, i) => (
              <div key={i} className="bg-white p-6 rounded-lg border border-[#EAE7E1]"><h4 className="font-medium text-[#1A362D] mb-2">{s.t}</h4><p className="text-[#4A5D56]">{s.d}</p></div>
            ))}
          </div>
          <div className="bg-[#D4AF37]/10 p-6 rounded-lg border border-[#D4AF37]/30 my-8">
            <h4 className="font-medium text-[#1A362D] mb-2">The "Tax Torpedo"</h4>
            <p className="text-[#4A5D56]">There's a zone where each additional dollar of income can trigger $1.85 in additional taxable income. This happens in the 50% inclusion range and creates an effective marginal rate far higher than your stated bracket.</p>
          </div>
          <AdUnit slot="social-security-mid" format="horizontal" />
          <AffiliateBanner type="software" />
          <div className="mt-8 p-8 bg-[#1A362D] rounded-xl text-center">
            <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Calculate Your Social Security Tax Impact</h3>
            <Button onClick={() => navigate("/calculators")} className="bg-white text-[#1A362D] hover:bg-white/90 h-12 px-8" data-testid="ss-page-calculator-btn">Use the SS Calculator <Calculator className="ml-2 w-5 h-5" /></Button>
          </div>
        </div>
      </section>
      <NewsletterSignup />
    </div>
  );
};

const RMDPlanningPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">Tax Planning Strategy</p>
          <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>RMD Planning Guide</h1>
          <p className="text-lg text-white/80 max-w-2xl">After age 73 the IRS requires withdrawals from most retirement accounts. Learn how to manage RMDs effectively.</p>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Understanding Required Minimum Distributions</h2>
          <p className="text-[#4A5D56] mb-6 leading-relaxed">Required Minimum Distributions (RMDs) are mandatory withdrawals from traditional IRAs, 401(k)s, and similar accounts. The IRS requires these to ensure tax-deferred savings eventually get taxed.</p>
          <div className="bg-white p-6 rounded-lg border border-[#EAE7E1] mb-8">
            <h3 className="font-medium text-[#1A362D] mb-4">Key RMD Rules for 2025</h3>
            <ul className="space-y-3 text-[#4A5D56]">{["RMDs begin at age 73 (SECURE 2.0 Act)", "First deadline: April 1 of the year after turning 73", "Subsequent RMDs: December 31 each year", "Penalty for missing: 25% (reduced from 50%)", "Roth IRAs have NO RMDs during owner's lifetime", "Inherited IRAs have different rules under SECURE Act"].map((r, i) => (
              <li key={i} className="flex items-start gap-3"><ChevronRight className="w-5 h-5 text-[#1A362D] flex-shrink-0 mt-0.5" /><span>{r}</span></li>
            ))}</ul>
          </div>
          <h2 className="text-2xl md:text-3xl font-medium text-[#1A362D] mb-6 mt-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Strategies to Manage RMDs</h2>
          <div className="space-y-6">
            {[{ t: "Roth Conversions Before RMDs Begin", d: "Converting before 73 reduces future RMD amounts since Roth IRAs don't have RMDs." }, { t: "Qualified Charitable Distributions (QCDs)", d: "Donate up to $105,000 (2025) directly from IRA to charity. Counts toward RMD, not included in taxable income." }, { t: "Strategic Timing", d: "Don't delay first RMD to April 1—this forces two RMDs in one year. Take it by December 31." }, { t: "Account Aggregation", d: "Aggregate RMDs from multiple traditional IRAs and take total from one account. 401(k)s must be separate." }, { t: "Reinvest in Taxable Account", d: "If you don't need the income, reinvest RMDs in a taxable brokerage account for continued growth." }].map((s, i) => (
              <div key={i} className="bg-white p-6 rounded-lg border border-[#EAE7E1]"><h4 className="font-medium text-[#1A362D] mb-2">{s.t}</h4><p className="text-[#4A5D56]">{s.d}</p></div>
            ))}
          </div>
          <AdUnit slot="rmd-mid" format="horizontal" />
          <AffiliateBanner type="book" />
          <div className="mt-8 p-8 bg-[#1A362D] rounded-xl text-center">
            <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Calculate Your RMD</h3>
            <Button onClick={() => navigate("/calculators")} className="bg-white text-[#1A362D] hover:bg-white/90 h-12 px-8" data-testid="rmd-page-calculator-btn">Use the RMD Calculator <Calculator className="ml-2 w-5 h-5" /></Button>
          </div>
        </div>
      </section>
      <NewsletterSignup />
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) { toast.error("Please fill in all required fields."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! Your message has been sent. Check your email for a confirmation.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Failed to send message. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Us</h1>
          <p className="text-lg text-white/80 max-w-2xl">Have questions about retirement tax planning? We're here to help.</p>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Card className="bg-white border-[#EAE7E1]">
            <CardHeader>
              <CardTitle className="text-2xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Send us a message</CardTitle>
              <CardDescription className="text-[#4A5D56]">We'll get back to you within 24 hours. You'll also receive an auto-confirmation email.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><Label>Name *</Label><Input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your name" className="h-12 border-[#EAE7E1]" data-testid="contact-name-input" /></div>
                  <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" className="h-12 border-[#EAE7E1]" data-testid="contact-email-input" /></div>
                </div>
                <div><Label>Phone (optional)</Label><Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" className="h-12 border-[#EAE7E1]" data-testid="contact-phone-input" /></div>
                <div><Label>Message *</Label><Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="How can we help?" rows={5} className="border-[#EAE7E1]" data-testid="contact-message-input" /></div>
                <Button type="submit" disabled={loading} className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white h-12 w-full md:w-auto px-8" data-testid="contact-submit-btn">
                  {loading ? "Sending..." : "Send Message"} <Send className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
      <NewsletterSignup />
    </div>
  );
};

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        await axios.post(`${API}/seed-blog`).catch(() => {});
        await axios.post(`${API}/seed-blog-additional`).catch(() => {});
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data);
      } catch { } finally { setLoading(false); }
    };
    fetchPosts();
  }, []);

  const categories = ["All", ...new Set(posts.map(p => p.category))];

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === "All" || post.category === activeCategory;
    const matchesSearch = !searchQuery || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="text-4xl md:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tax Planning Blog</h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">Expert insights on retirement tax planning, Roth conversions, and more.</p>
          {/* Search Bar */}
          <div className="max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7B8D85]" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-12 pl-12 pr-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent backdrop-blur-sm"
              data-testid="blog-search-input"
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-6 mb-8 -mx-1 px-1">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-[#1A362D] text-white shadow-md'
                    : 'bg-white text-[#4A5D56] border border-[#EAE7E1] hover:border-[#1A362D]/30 hover:bg-[#F9F8F6]'
                }`}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
                {cat !== "All" && <span className="ml-1.5 text-xs opacity-60">({posts.filter(p => p.category === cat).length})</span>}
              </button>
            ))}
          </div>

          {/* Results Count */}
          {(searchQuery || activeCategory !== "All") && (
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#7B8D85]" data-testid="blog-results-count">
                {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
                {searchQuery && <span> for "<strong className="text-[#1A362D]">{searchQuery}</strong>"</span>}
                {activeCategory !== "All" && <span> in <strong className="text-[#1A362D]">{activeCategory}</strong></span>}
              </p>
              {(searchQuery || activeCategory !== "All") && (
                <button
                  onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}
                  className="text-sm text-[#1A362D] hover:underline"
                  data-testid="blog-clear-filters"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12"><p className="text-[#7B8D85]">Loading articles...</p></div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-16 h-16 text-[#EAE7E1] mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl text-[#1A362D] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>No articles found</h3>
              <p className="text-[#7B8D85] mb-4">Try a different search term or category.</p>
              <button onClick={() => { setSearchQuery(""); setActiveCategory("All"); }} className="text-[#1A362D] font-medium hover:underline" data-testid="blog-show-all">Show all articles</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="bg-white border-[#EAE7E1] hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden" onClick={() => navigate(`/blog/${post.slug}`)} data-testid={`blog-card-${post.slug}`}>
                  {post.image_url && <div className="aspect-video overflow-hidden"><img src={post.image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>}
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2"><span className="text-xs tracking-[0.1em] uppercase font-semibold text-[#4A6B5D]">{post.category}</span><span className="text-[#7B8D85]">·</span><span className="text-xs text-[#7B8D85]">{post.read_time} min</span></div>
                    <CardTitle className="text-xl text-[#1A362D] group-hover:text-[#4A6B5D] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{post.title}</CardTitle>
                  </CardHeader>
                  <CardContent><CardDescription className="text-[#4A5D56]">{post.excerpt}</CardDescription><div className="mt-4 flex items-center text-[#1A362D] font-medium group-hover:text-[#4A6B5D]">Read Article <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></div></CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-6 md:px-12"><AdUnit slot="blog-listing" format="horizontal" /></div>
      <NewsletterSignup />
    </div>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [postRes, allRes] = await Promise.all([
          axios.get(`${API}/blog/${slug}`),
          axios.get(`${API}/blog`)
        ]);
        const currentPost = postRes.data;
        setPost(currentPost);

        // Find related: same category first, then others, exclude current
        const others = allRes.data.filter(p => p.slug !== slug);
        const sameCategory = others.filter(p => p.category === currentPost.category);
        const diffCategory = others.filter(p => p.category !== currentPost.category);
        const related = [...sameCategory, ...diffCategory].slice(0, 3);
        setRelatedPosts(related);
      } catch { }
      finally { setLoading(false); }
    };
    fetchData();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center"><p className="text-[#7B8D85]">Loading article...</p></div>;
  if (!post) return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center"><div className="text-center"><h2 className="text-2xl text-[#1A362D] mb-4">Article not found</h2><Button onClick={() => navigate("/blog")} className="bg-[#1A362D] text-white">Back to Blog</Button></div></div>;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <section className="relative py-20 bg-[#1A362D]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <button onClick={() => navigate("/blog")} className="text-white/70 hover:text-white mb-6 flex items-center gap-2" data-testid="back-to-blog"><ChevronRight className="w-4 h-4 rotate-180" />Back to Blog</button>
          <div className="flex items-center gap-2 mb-4"><span className="text-xs tracking-[0.1em] uppercase font-semibold text-[#D4AF37]">{post.category}</span><span className="text-white/50">·</span><span className="text-sm text-white/70">{post.read_time} min read</span></div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{post.title}</h1>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {post.image_url && <img src={post.image_url} alt={post.title} className="w-full rounded-xl mb-12 shadow-lg" />}
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
          <AdUnit slot="blog-post-mid" format="horizontal" />
          <AffiliateBanner type="advisor" />
          <div className="mt-8 p-8 bg-[#1A362D] rounded-xl text-center">
            <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ready to put this knowledge to use?</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/calculators")} className="bg-white text-[#1A362D] hover:bg-white/90 h-12 px-8" data-testid="blog-post-calculator-btn">Try Our Calculators</Button>
              <Button onClick={() => navigate("/guides")} className="bg-[#D4AF37] text-[#1A362D] hover:bg-[#D4AF37]/90 h-12 px-8" data-testid="blog-post-guides-btn">Download Free Guide</Button>
            </div>
          </div>

          {/* Related Articles */}
          {relatedPosts.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-medium text-[#1A362D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                You Might Also Like
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((rp) => (
                  <Card
                    key={rp.id}
                    className="bg-white border-[#EAE7E1] hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden"
                    onClick={() => navigate(`/blog/${rp.slug}`)}
                    data-testid={`related-article-${rp.slug}`}
                  >
                    {rp.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img src={rp.image_url} alt={rp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#4A6B5D]">{rp.category}</span>
                        <span className="text-[#7B8D85]">·</span>
                        <span className="text-[10px] text-[#7B8D85]">{rp.read_time} min</span>
                      </div>
                      <CardTitle className="text-base text-[#1A362D] group-hover:text-[#4A6B5D] transition-colors leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {rp.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center text-sm text-[#1A362D] font-medium group-hover:text-[#4A6B5D]">
                        Read <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <NewsletterSignup />
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dripDetails, setDripDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.authenticated) { setAuthenticated(true); toast.success("Welcome to the admin dashboard!"); }
    } catch { toast.error("Invalid password."); }
  };

  const fetchData = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const [ov, sub, con, dl, pay, drip] = await Promise.all([
        axios.get(`${API}/admin/overview`),
        axios.get(`${API}/admin/subscribers`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/downloads`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/drip-details`)
      ]);
      setOverview(ov.data);
      setSubscribers(sub.data);
      setContacts(con.data);
      setDownloads(dl.data);
      setPayments(pay.data);
      setDripDetails(drip.data);
    } catch { toast.error("Failed to load data."); }
    finally { setLoading(false); }
  }, [authenticated]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Card className="w-full max-w-sm bg-white border-[#EAE7E1]">
          <CardHeader>
            <CardTitle className="text-xl text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Admin Dashboard</CardTitle>
            <CardDescription>Enter your admin password to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" className="h-12 border-[#EAE7E1]" data-testid="admin-password-input" />
              <Button type="submit" className="bg-[#1A362D] hover:bg-[#4A6B5D] text-white w-full h-12" data-testid="admin-login-btn">Login</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StatCard = ({ label, value, sub, color = "bg-[#F9F8F6]" }) => (
    <div className={`${color} p-5 rounded-xl border border-[#EAE7E1]`}>
      <p className="text-xs tracking-[0.1em] uppercase text-[#7B8D85] mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${color === "bg-[#1A362D]" ? "text-white" : "text-[#1A362D]"}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${color === "bg-[#1A362D]" ? "text-white/60" : "text-[#7B8D85]"}`}>{sub}</p>}
    </div>
  );

  const ExportButton = ({ endpoint, filename }) => {
    const handleExport = async () => {
      try {
        const res = await axios.get(`${API}/admin/export/${endpoint}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${filename}.csv downloaded!`);
      } catch { toast.error("Export failed."); }
    };
    return (
      <Button onClick={handleExport} variant="outline" className="border-[#1A362D] text-[#1A362D] hover:bg-[#1A362D] hover:text-white h-9 text-xs" data-testid={`export-${endpoint}-btn`}>
        <Download className="w-3 h-3 mr-2" /> Export CSV
      </Button>
    );
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "subscribers", label: `Subscribers (${subscribers.length})` },
    { id: "contacts", label: `Contacts (${contacts.length})` },
    { id: "downloads", label: `Downloads (${downloads.length})` },
    { id: "payments", label: `Payments (${payments.length})` },
    { id: "drip", label: `Drip Sequences (${dripDetails.length})` }
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="bg-[#1A362D] py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <h1 className="text-2xl font-medium text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Admin Dashboard</h1>
          <Button variant="outline" onClick={fetchData} className="border-white/30 text-white hover:bg-white/10 bg-transparent" data-testid="admin-refresh-btn">
            {loading ? "Loading..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Tab Nav */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${activeTab === t.id ? 'bg-[#1A362D] text-white' : 'bg-white text-[#4A5D56] border border-[#EAE7E1] hover:bg-[#EAE7E1]'}`} data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && overview && (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Funnel Metrics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Newsletter Subscribers" value={overview.newsletter_subscribers} />
                <StatCard label="PDF Downloads" value={overview.pdf_downloads} />
                <StatCard label="Premium Users" value={overview.premium_users} />
                <StatCard label="Contact Submissions" value={overview.contact_submissions} />
              </div>
            </div>
            <div>
              <h2 className="text-lg font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Revenue</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Revenue" value={`$${overview.payments.revenue.toFixed(2)}`} color="bg-[#1A362D]" />
                <StatCard label="Paid Transactions" value={overview.payments.paid} sub={`of ${overview.payments.total} total`} />
                <StatCard label="Pending Payments" value={overview.payments.pending} />
                <StatCard label="Blog Posts" value={overview.blog_posts} />
              </div>
            </div>
            <div>
              <h2 className="text-lg font-medium text-[#1A362D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Email Drip Sequence</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard label="Drip Subscribers" value={overview.drip.subscribers} />
                <StatCard label="Emails Scheduled" value={overview.drip.total_scheduled} />
                <StatCard label="Sent" value={overview.drip.sent} />
                <StatCard label="Pending" value={overview.drip.pending} />
                <StatCard label="Failed" value={overview.drip.failed} />
              </div>
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Newsletter Subscribers</h2>
              <ExportButton endpoint="subscribers" filename="subscribers" />
            </div>
            <Card className="bg-white border-[#EAE7E1]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#EAE7E1] bg-[#F9F8F6]"><th className="text-left p-4 text-[#7B8D85] font-medium">Email</th><th className="text-left p-4 text-[#7B8D85] font-medium">Subscribed At</th></tr></thead>
                  <tbody>
                    {subscribers.map((s, i) => (
                      <tr key={i} className="border-b border-[#EAE7E1]/50 hover:bg-[#F9F8F6]">
                        <td className="p-4 text-[#1A362D]">{s.email}</td>
                        <td className="p-4 text-[#4A5D56]">{formatDate(s.subscribed_at)}</td>
                      </tr>
                    ))}
                    {subscribers.length === 0 && <tr><td colSpan={2} className="p-8 text-center text-[#7B8D85]">No subscribers yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Submissions</h2>
              <ExportButton endpoint="contacts" filename="contacts" />
            </div>
            <Card className="bg-white border-[#EAE7E1]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#EAE7E1] bg-[#F9F8F6]"><th className="text-left p-4 text-[#7B8D85] font-medium">Name</th><th className="text-left p-4 text-[#7B8D85] font-medium">Email</th><th className="text-left p-4 text-[#7B8D85] font-medium">Message</th><th className="text-left p-4 text-[#7B8D85] font-medium">Date</th></tr></thead>
                  <tbody>
                    {contacts.map((c, i) => (
                      <tr key={i} className="border-b border-[#EAE7E1]/50 hover:bg-[#F9F8F6]">
                        <td className="p-4 text-[#1A362D] font-medium">{c.name}</td>
                        <td className="p-4 text-[#4A5D56]">{c.email}</td>
                        <td className="p-4 text-[#4A5D56] max-w-xs truncate">{c.message}</td>
                        <td className="p-4 text-[#7B8D85] whitespace-nowrap">{formatDate(c.created_at)}</td>
                      </tr>
                    ))}
                    {contacts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#7B8D85]">No contacts yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Downloads Tab */}
        {activeTab === "downloads" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>PDF Downloads</h2>
              <ExportButton endpoint="downloads" filename="downloads" />
            </div>
            <Card className="bg-white border-[#EAE7E1]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#EAE7E1] bg-[#F9F8F6]"><th className="text-left p-4 text-[#7B8D85] font-medium">Email</th><th className="text-left p-4 text-[#7B8D85] font-medium">Product</th><th className="text-left p-4 text-[#7B8D85] font-medium">Downloaded At</th></tr></thead>
                  <tbody>
                    {downloads.map((d, i) => (
                      <tr key={i} className="border-b border-[#EAE7E1]/50 hover:bg-[#F9F8F6]">
                        <td className="p-4 text-[#1A362D]">{d.email}</td>
                        <td className="p-4 text-[#4A5D56]">{d.product}</td>
                        <td className="p-4 text-[#7B8D85]">{formatDate(d.downloaded_at)}</td>
                      </tr>
                    ))}
                    {downloads.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-[#7B8D85]">No downloads yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Payment Transactions</h2>
              <ExportButton endpoint="payments" filename="payments" />
            </div>
            <Card className="bg-white border-[#EAE7E1]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#EAE7E1] bg-[#F9F8F6]"><th className="text-left p-4 text-[#7B8D85] font-medium">Product</th><th className="text-left p-4 text-[#7B8D85] font-medium">Email</th><th className="text-right p-4 text-[#7B8D85] font-medium">Amount</th><th className="text-left p-4 text-[#7B8D85] font-medium">Status</th><th className="text-left p-4 text-[#7B8D85] font-medium">Date</th></tr></thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={i} className="border-b border-[#EAE7E1]/50 hover:bg-[#F9F8F6]">
                        <td className="p-4 text-[#1A362D] font-medium">{p.product_name || p.package_name || "—"}</td>
                        <td className="p-4 text-[#4A5D56]">{p.customer_email || "—"}</td>
                        <td className="p-4 text-right text-[#1A362D] font-medium">${p.amount?.toFixed(2)}</td>
                        <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${p.payment_status === 'paid' ? 'bg-green-100 text-green-800' : p.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{p.payment_status}</span></td>
                        <td className="p-4 text-[#7B8D85] whitespace-nowrap">{formatDate(p.created_at)}</td>
                      </tr>
                    ))}
                    {payments.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#7B8D85]">No payments yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Drip Sequences Tab */}
        {activeTab === "drip" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-[#1A362D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Drip Sequences</h2>
              <ExportButton endpoint="drip" filename="drip_sequences" />
            </div>
            <div className="space-y-4">
            {dripDetails.map((d, i) => (
              <Card key={i} className="bg-white border-[#EAE7E1]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[#1A362D] font-medium">{d.email}</span>
                    <span className="text-sm bg-[#EAE7E1] text-[#1A362D] px-3 py-1 rounded-full">{d.progress} sent</span>
                  </div>
                  <div className="flex gap-2">
                    {d.entries.map((e, j) => (
                      <div key={j} className={`flex-1 h-2 rounded-full ${e.status === 'sent' ? 'bg-[#4A6B5D]' : e.status === 'pending' ? 'bg-[#EAE7E1]' : 'bg-red-300'}`} title={`Email #${e.sequence_number}: ${e.status}`} />
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {d.entries.map((e, j) => (
                      <span key={j} className={`flex-1 text-center text-[10px] ${e.status === 'sent' ? 'text-[#4A6B5D]' : e.status === 'pending' ? 'text-[#7B8D85]' : 'text-red-500'}`}>
                        #{e.sequence_number}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            {dripDetails.length === 0 && <p className="text-center text-[#7B8D85] py-8">No drip sequences yet.</p>}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/calculators" element={<CalculatorsPage />} />
          <Route path="/roth-strategies" element={<RothStrategiesPage />} />
          <Route path="/roth-conversion-strategies" element={<RothStrategiesPage />} />
          <Route path="/social-security" element={<SocialSecurityPage />} />
          <Route path="/social-security-tax-planning" element={<SocialSecurityPage />} />
          <Route path="/rmd-planning" element={<RMDPlanningPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/find-advisor" element={<FindAdvisorPage />} />
          <Route path="/download/success" element={<DownloadSuccessPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
        <Footer />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
